/**
 * What TWO can do today, and what is announced for later. One list drives the honest
 * suggestion chips in the web app, the capability section of the system prompt, the
 * "Not yet." reply shape and the about-TWO knowledge, so the product never suggests
 * something TWO cannot do.
 */
export type CapabilityId =
  | 'conversation'
  | 'memory'
  | 'drafting'
  | 'voice'
  | 'images'
  | 'documents'
  | 'links'
  | 'calendar'
  | 'mail'
  | 'flights'
  | 'attention'
  | 'reminders'
  | 'monitors'
  | 'brief';

/** Product surface a capability arrives with (You → …). */
export type ArrivesWith = 'connections' | 'permissions' | 'tasks';

export interface Capability {
  id: CapabilityId;
  /** True when the current build can honour requests for it. */
  available: boolean;
  /** Suggestion chip key (i18n `two.chips.<chip>`), when the capability suggests one. */
  chip?: string;
  /** Further chips the capability suggests (only when available). */
  chips?: string[];
  /** For unavailable capabilities: the surface it arrives with, for the "Not yet." sentence. */
  arrivesWith?: ArrivesWith;
  /** Short English description used in the system prompt. */
  summary: string;
}

export const CAPABILITIES: readonly Capability[] = [
  {
    id: 'conversation',
    available: true,
    summary: 'conversation: answer, explain, think something through with the person',
  },
  {
    id: 'memory',
    available: true,
    summary:
      'memory: remember what the person states about themselves (preferences, routines, people, places, projects, how they like TWO to behave), show it under You → What TWO knows, correct or forget it on request, and use it as context, never as permission',
  },
  {
    id: 'drafting',
    available: true,
    summary: 'drafting text: messages, emails, replies and notes the person sends themselves',
  },
  {
    id: 'voice',
    available: true,
    chip: 'voice',
    summary: 'voice notes: the person speaks, you receive the transcript and answer it',
  },
  {
    id: 'images',
    available: true,
    chip: 'photo',
    summary:
      'images: photos of receipts, tickets, screenshots, documents and menus; you read them and extract the facts',
  },
  {
    id: 'documents',
    available: true,
    summary: 'documents: PDF files up to 100 pages; you read them and answer from their content',
  },
  {
    id: 'links',
    available: true,
    chip: 'link',
    summary: 'links: any http(s) link in a message is fetched and read so you can answer from it',
  },
  {
    id: 'reminders',
    available: true,
    chip: 'remind',
    summary:
      'reminders: the person asks to be reminded of something at a time (either language); TWO confirms with the resolved day and time and says it when due, in the app or by email when they are away and asked for that',
  },
  {
    id: 'calendar',
    available: false,
    chip: 'plan',
    arrivesWith: 'connections',
    summary:
      'calendar: reading the person\x27s connected calendar, finding free slots, creating and moving events through the permission levels they set',
  },
  {
    id: 'mail',
    available: false,
    chip: 'reply',
    arrivesWith: 'connections',
    summary:
      'mail: searching and reading the person\x27s connected mail, drafting replies and sending them through the permission levels they set (sending always asks unless they set otherwise), and looking up contacts',
  },
  {
    id: 'flights',
    available: false,
    chip: 'flight',
    arrivesWith: 'connections',
    summary: 'flight search and booking',
  },
  {
    id: 'attention',
    available: false,
    chip: 'attention',
    arrivesWith: 'tasks',
    summary: 'watching accounts and telling the person what needs their attention',
  },
  {
    id: 'monitors',
    available: false,
    arrivesWith: 'connections',
    summary:
      'monitors: watching the connected mail for one thing ("tell me when Alex replies") and saying once when it arrives; after an invite, watching for the attendee\x27s reply for two days',
  },
  {
    id: 'brief',
    available: false,
    arrivesWith: 'connections',
    summary:
      'daily brief: when the person turns it on under You → Account, one short message each morning from today\x27s calendar, waiting approvals, reminders due and what arrived overnight; nothing when there is nothing',
  },
];

/** Capabilities that arrive with Connections; available once the person has one connected account. */
export const CONNECTED_CAPABILITIES: readonly CapabilityId[] = [
  'calendar',
  'mail',
  'monitors',
  'brief',
];

/** Capabilities that need a mailbox on the connected account (M4.5: probed at connect time). */
export const MAIL_CAPABILITIES: readonly CapabilityId[] = ['mail', 'monitors'];

/**
 * The capability list for one person: calendar and mail become available when at least one
 * account is connected (with the chips "plan" / "slot" / "reply"), and stay announced otherwise.
 * `mail: false` (a connected account without a mailbox) keeps mail and monitors unavailable,
 * and their summaries say that another account is needed.
 */
export function capabilitiesFor(person: { connected: boolean; mail?: boolean }): Capability[] {
  const mail = person.mail ?? true;
  return CAPABILITIES.flatMap((c) => {
    if (!CONNECTED_CAPABILITIES.includes(c.id)) return [c];
    if (!person.connected) return [c];
    if (MAIL_CAPABILITIES.includes(c.id) && !mail) {
      return [
        {
          ...c,
          available: false,
          summary: `${c.summary} (the connected account has no mailbox: mail needs another account, Microsoft or a Gmail account, under Connections)`,
        },
      ];
    }
    const { arrivesWith: _arrives, ...rest } = c;
    return [{ ...rest, available: true, ...(c.id === 'calendar' ? { chips: ['slot'] } : {}) }];
  });
}

export function availableCapabilities(list: readonly Capability[] = CAPABILITIES): Capability[] {
  return list.filter((c) => c.available);
}

export function pendingCapabilities(list: readonly Capability[] = CAPABILITIES): Capability[] {
  return list.filter((c) => !c.available);
}

/** Chip keys for the empty conversation, derived from what is available right now. */
export function suggestionChips(list: readonly Capability[] = CAPABILITIES): string[] {
  return availableCapabilities(list).flatMap((c) => [
    ...(c.chip ? [c.chip] : []),
    ...(c.chips ?? []),
  ]);
}
